/**
 * I mattoni dei form di tiro: campo con etichetta, elenco di regolamento,
 * riquadro per domanda.
 *
 * Erano dentro `ShootingForm.js` e il form della libreria drill ne faceva a
 * meno — da cui campi senza etichetta nella libreria e con l'etichetta nel
 * programma, cioè «i due designer devono comportarsi uguali». Stanno qui
 * perché li usano entrambi.
 */
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import {
	START_CONDITION_FIREARM,
	START_CONDITION_SHOOTER,
} from "../../lib/programBuilderUtils";

/**
 * Un campo con **etichetta persistente**.
 *
 * Il placeholder sparisce alla prima lettera: era il motivo per cui, a metà
 * di un campo, non si sapeva più cosa si stesse compilando. Qui l'etichetta
 * resta, e il placeholder torna a fare il suo mestiere — un esempio.
 */
export function Field({ label, hint, children, className = "" }) {
	return (
		<div className={`space-y-1 ${className}`}>
			<Label className="label-tactical">{label}</Label>
			{children}
			{hint && (
				<p className="text-[10px] text-muted-foreground leading-snug">{hint}</p>
			)}
		</div>
	);
}

/**
 * Un elenco di valori di regolamento, con la via d'uscita del testo libero.
 *
 * Le condizioni di partenza le descrive il regolamento (8.1 e 8.2): metterle
 * in elenco è ciò che le rende uguali su due drill diversi. Ma il regolamento
 * non le enumera tutte — "come da briefing" copre il resto — quindi la voce
 * libera non è un ripiego, è prevista.
 */
export function RegulationSelect({
	label,
	hint,
	options,
	value,
	onChange,
	placeholder,
	t,
	className = "bg-secondary border-transparent rounded-sm",
}) {
	const known = options.some((option) => option.value === value);
	const isCustom = !!value && !known;
	return (
		<Field label={label} hint={hint}>
			<Select
				value={isCustom ? "__custom__" : value || "__none__"}
				onValueChange={(next) => {
					if (next === "__none__") return onChange(null);
					if (next === "__custom__") return onChange(" ");
					onChange(next);
				}}
			>
				<SelectTrigger className={className}>
					<SelectValue placeholder={placeholder} />
				</SelectTrigger>
				<SelectContent className="bg-card border-border">
					<SelectItem value="__none__">{t("pbShootNotIndicated")}</SelectItem>
					{options.map((option) => (
						<SelectItem key={option.value} value={option.value}>
							{option.label}
						</SelectItem>
					))}
					<SelectItem value="__custom__">{t("pbShootCustomOption")}</SelectItem>
				</SelectContent>
			</Select>
			{isCustom && (
				<Input
					autoFocus
					value={value.trim() === "" ? "" : value}
					onChange={(e) => onChange(e.target.value || " ")}
					className={`${className} mt-1`}
					placeholder={t("pbShootDescribeCondition")}
				/>
			)}
		</Field>
	);
}

/** Un riquadro con un titolo: raggruppa per domanda, non per tipo di campo. */
export function Section({ title, hint, children, action, className = "" }) {
	return (
		<div
			className={`rounded-sm border border-border/70 bg-secondary/20 p-3 space-y-2.5 ${className}`}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="min-w-0">
					<p className="text-xs font-semibold uppercase tracking-wider text-volt">
						{title}
					</p>
					{hint && (
						<p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>
					)}
				</div>
				{action}
			</div>
			{children}
		</div>
	);
}

/**
 * Le **condizioni di partenza**: com'è l'arma e com'è il tiratore allo start.
 *
 * Stanno in chiaro, accanto agli altri campi principali, e non dietro «altri
 * dettagli»: su un drill di tiro non sono una rifinitura, sono parte della
 * descrizione dell'esercizio — «carica in fondina, mani sopra le spalle» e
 * «già in mano, mire sul bersaglio» sono due esercizi diversi anche a parità
 * di bersagli, volume e par time.
 *
 * Una sola, per il ProgramBuilder e per la libreria drill.
 */
export function StartConditionsSection({
	shooting = {},
	onChange,
	t,
	className = "bg-background border-transparent rounded-sm",
}) {
	return (
		<Section
			title={t("pbShootStartSection")}
			hint={t("pbShootStartSectionHint")}
		>
			<div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
				<RegulationSelect
					label={t("pbShootStartFirearm")}
					hint={t("pbShootStartFirearmHint")}
					options={START_CONDITION_FIREARM}
					// `start_position` è il campo unico di prima: un drill salvato
					// allora deve continuare a mostrare ciò che ha dentro.
					value={
						shooting.start_condition_firearm ?? shooting.start_position ?? null
					}
					onChange={(v) =>
						onChange({ start_condition_firearm: v, start_position: null })
					}
					placeholder={t("pbShootStartFirearm")}
					className={className}
					t={t}
				/>
				<RegulationSelect
					label={t("pbShootStartShooter")}
					hint={t("pbShootStartShooterHint")}
					options={START_CONDITION_SHOOTER}
					value={shooting.start_condition_shooter || null}
					onChange={(v) => onChange({ start_condition_shooter: v })}
					placeholder={t("pbShootStartShooter")}
					className={className}
					t={t}
				/>
			</div>
		</Section>
	);
}

/**
 * Zone e penalità di un sistema di punteggio, in chiaro: `A 5/5 · C 4/3 ·
 * D 2/1 · M −10`. Il nome da solo non dice cosa si è scelto, ed è la stessa
 * tabella che il tiratore si troverà a compilare.
 */
export function ScoringSystemZones({ system }) {
	if (!system) return null;
	const zones = (system.zones || []).map((zone) => {
		const major = zone.points_major ?? zone.points;
		const minor = zone.points_minor ?? zone.points;
		if (zone.seconds !== null && zone.seconds !== undefined)
			return `${zone.code} +${zone.seconds}s`;
		return major === minor ? `${zone.code} ${major}` : `${zone.code} ${major}/${minor}`;
	});
	const penalties = (system.penalties || []).map((p) =>
		p.seconds ? `${p.code} +${p.seconds}s` : `${p.code} ${p.points}`,
	);
	return (
		<p className="text-[11px] text-muted-foreground leading-snug">
			{zones.join(" · ")}
			{penalties.length > 0 && (
				<span className="text-red-400/90">
					{" · "}
					{penalties.join(" · ")}
				</span>
			)}
		</p>
	);
}
